/**
 * Vortex Tank — Void portal control. Place portal pairs with S1,
 * tentacles auto-defend portals, Anti-Gravity Beam (S2) carves terrain
 * and detonates portals.
 */

import { registerWeapon } from '../weapons';
import { registerTank } from './registry';
import { SMALL_CRATER } from '../terrain/crater-shapes';
import { placeVortexPortalPair } from '../game/vortex-holes';

// ─── Shot 1: Void Portal — arc projectile, creates portal pair on impact ────

const VORTEX_SHOT1 = registerWeapon({
  id: 'vortex_shot1',
  name: 'Void Portal',
  description: 'Creates a portal pair — one at impact, one behind Vortex. Tentacles defend portals.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 8,
  projectileSpeed: 0.55,
  color: 0x9944ff,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 7,
  blastRadius: 50,
  knockbackMult: 0.04,
  knockbackRadius: 1.0,
  shakeIntensity: 3.0,
  particleScale: 1.0,
  craterShape: SMALL_CRATER,
  muzzleEffect: 'spawnVortexVoidTear',
  edgeguard: { chargeFrames: 35, threshold: 40 },
  onTick(proj, _state, fx) {
    // Void energy trail — pulsing purple shimmer
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    const t = proj.behaviorState.t;
    if (t % 2 === 0) {
      const pulse = 0.6 + Math.sin(t * 0.25) * 0.3;
      // Purple energy wisps
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 10,
        proj.y + (Math.random() - 0.5) * 10,
        Math.random() < 0.7 ? 0x9944ff : 0xaa66ff,
        0.3, 1.5, 0.08, 0.15,
        0.25 * pulse, 0.5 * pulse,
        0, 0.92, true, false, 0.5 * pulse,
      ] });
      // Dark void wisps (Sakurai: dark contrast)
      if (t % 4 === 0) {
        fx.push({ type: 'particles', method: 'spawnParticle', args: [
          proj.x + (Math.random() - 0.5) * 8,
          proj.y + (Math.random() - 0.5) * 8,
          0x110022,
          0.2, 1.0, 0.1, 0.18,
          0.3 * pulse, 0.6 * pulse,
          0, 0.9, false, false, 0.6,
        ] });
      }
    }
    return null;
  },
  onImpact(proj, x, y, _hitType, _hitIndex, state, fx) {
    // Portal spawn VFX — purple energy burst at impact
    fx.push({ type: 'particles', method: 'spawnBouncerBankShot', args: [x, y, 0x9944ff, 1.2] });
    placeVortexPortalPair(state, x, y, proj.owner, fx);
    return [];
  },
});

// ─── Shot 2: Anti-Gravity Beam — terrain-carving hitscan, detonates portals ──

const VORTEX_SHOT2 = registerWeapon({
  id: 'vortex_shot2',
  name: 'Anti-Gravity Beam',
  description: 'Terrain-carving beam that pulls enemies toward Vortex. Detonates portals it crosses.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 4,
  projectileSpeed: 1.0,
  color: 0x9944ff,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 0,
  blastRadius: 0,
  knockbackMult: 0,
  knockbackRadius: 0,
  shakeIntensity: 0,
  particleScale: 0,
  craterShape: SMALL_CRATER,
  hitscan: true,
  tracerMode: 'hitscan',
  edgeguard: { chargeFrames: 35, threshold: 40 },
});

registerTank({
  id: 'vortex',
  name: 'Vortex',
  description: 'Void portal control. Place portals, fire the Anti-Gravity Beam to detonate them.',
  visual: { bodyColor: 0x6622AA, glowColor: 0x9944FF, bodyRadius: 38 },
  ecbRadius: 32,
  bonus: true,
  chargeSize: 115,
  landingMultiplier: 1.1,
  shots: [
    { tier: 1, weapon: VORTEX_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Void Portal', tierColor: 0x9944ff },
    { tier: 2, weapon: VORTEX_SHOT2, momentumCost: 0, hitlagBonus: 0, label: 'Anti-Grav Beam', tierColor: 0x9944ff },
  ],
});
